import React, { useState } from 'react';
import './TopBarMenu.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useUser } from '../context/UserContext';
import { useMsal } from '@azure/msal-react';
import authService from '../services/authService';

const menuData = [
  {
    key: 'analytics',
    icon: 'fa-chart-bar',
    label: 'Analytics',
    color: '--analytics-border',
    items: [
      { icon: 'fa-th', title: 'Stock Performance', desc: 'Monitor stock levels and performance metrics.' },
      { icon: 'fa-globe', title: 'CO Instance', desc: 'Country stock performance.' },
      { icon: 'fa-database', title: 'Data warehouse', desc: 'Central repository for analytics data.' },
    ]
  },
  {
    key: 'operations',
    icon: 'fa-ticket',
    label: 'Operations',
    color: '--operations-border',
    items: [
      { icon: 'fa-map-marker-alt', title: 'Tracking', desc: 'Track and monitor operations progress.' },
      { icon: 'fa-ticket', title: 'Ticketing system', desc: 'Log and manage support tickets.' },
      { icon: 'fa-comments', title: 'CO feedback', desc: 'Collect feedback from country offices.' },
      { icon: 'fa-list-check', title: 'COE TA Tracker', desc: 'Country office technical assistance tracker.', path: '/resolution-matrices' },
    ]
  },
  {
    key: 'coldchain',
    icon: 'fa-temperature-low',
    label: 'Cold chain',
    color: '--coldchain-border',
    items: [
      { icon: 'fa-boxes-stacked', title: 'Inventories', desc: 'Available cold chain equipment inventory.' },
      { icon: 'fa-snowflake', title: 'UCC Inventories', desc: 'Inventory of Ultra-Low-Temperature Freezers (ULT-F).', path: '/ucc-inventories' },
      { icon: 'fa-thermometer-half', title: 'Temperature RTMD', desc: 'Real-time monitoring devices data.' },
      { icon: 'fa-chart-bar', title: 'Gaps', desc: 'Identify cold chain capacity gaps.' },
    ]
  },
  {
    key: 'fsp4all',
    icon: 'fa-layer-group',
    label: 'Policy & Planning',
    color: '--fsp4all-border',
    items: [
      { icon: 'fa-chart-pie', title: 'SCIP', desc: 'Supply chain improvement planning.' },
      { icon: 'fa-clipboard-check', title: 'EVMA', desc: 'Effective vaccine management assessment.' },
      { icon: 'fa-scale-balanced', title: 'NLWG maturity', desc: 'National logistics working group maturity.' },
      { icon: 'fa-motorcycle', title: 'DRIVE', desc: 'Last mile delivery dashboard.' },
      { icon: 'fa-chart-line', title: 'FSP4All Forecasting', desc: 'Advanced demand forecasting and supply planning.' },
    ]
  },
  {
    key: 'sustainability',
    icon: 'fa-sun',
    label: 'Sustainability',
    color: '--sustainability-border',
    items: [
      { icon: 'fa-solar-panel', title: 'Solarization', desc: 'Use of solar-powered solutions.' },
      { icon: 'fa-recycle', title: 'Waste Management', desc: 'Reduce, reuse and recycle waste.' },
      { icon: 'fa-leaf', title: 'Carbon Footprint', desc: 'Measure & offset CO2 emissions.' },
    ]
  },
  {
    key: 'coverage',
    icon: 'fa-syringe',
    label: 'Coverage & new vax',
    color: '--coverage-border',
    items: [
      { icon: 'fa-chart-line', title: 'Coverage data', desc: 'Immunization coverage statistics.' },
      { icon: 'fa-vial', title: 'New vaccine intro', desc: 'Introducing new vaccine programmes.' },
      { icon: 'fa-biohazard', title: 'Outbreaks', desc: 'Monitor and respond to outbreaks.' },
      { icon: 'fa-wallet', title: 'Budgets', desc: 'Plan and monitor immunization programme budgets.' },
    ]
  },
  {
    key: 'gavi',
    icon: 'fa-file-lines',
    label: 'GAVI Reporting',
    color: '--gavi-border',
    items: [
      { icon: 'fa-file-alt', title: 'GAVI Reporting Indicators', desc: 'Reporting indicators placeholder.' },
    ]
  }
];

const TopBar = ({ currentPage, userClaims }) => {
  const { user } = useUser();
  const { instance } = useMsal();

  const [currentLang, setCurrentLang] = useState(() => {
    try {
      const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
      if (match && match[1]) {
        const raw = decodeURIComponent(match[1]);
        const code = (raw.split('/').pop() || '').toLowerCase();
        if (['en','es','fr'].includes(code)) return code;
      }
    } catch (e) {}
    return 'en';
  });

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setCurrentLang(lang);
    const googtrans = `/en/${lang}`;
    const cookieName = 'googtrans';
    const expiry = new Date();
    expiry.setFullYear(expiry.getFullYear() + 1);
    const expires = `; expires=${expiry.toUTCString()}`;
    // Set for current host (works on localhost)
    document.cookie = `${cookieName}=${googtrans}${expires}; path=/`;
    // Also try with domain-scoped cookie for subdomains
    const host = window.location.hostname;
    if (!/^(localhost|127\.0\.0\.1)$/.test(host)) {
      document.cookie = `${cookieName}=${googtrans}${expires}; path=/; domain=.${host}`;
    }
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      await authService.logoutRedirect(instance);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };
  // Determine which tab should be open based on current page
  const getInitialTab = () => {
    if (!currentPage) return 'analytics';
    
    const pageToTabMap = {
      'stock-performance': 'analytics',
      'co-instance': 'analytics',
      'data-warehouse': 'analytics',
      'tracking': 'operations',
      'ticketing-system': 'operations',
      'co-feedback': 'operations',
      'resolution-matrices': 'operations',
      'inventories': 'coldchain',
      'ucc-inventories': 'coldchain',
      'temperature-rtmd': 'coldchain',
      'gaps': 'coldchain',
      'fsp4all': 'fsp4all',
      'scip': 'fsp4all',
      'scip-tool': 'fsp4all',
      'evma': 'fsp4all',
      'nlwg-maturity': 'fsp4all',
      'drive': 'fsp4all',
      'fsp4all-forecasting': 'fsp4all',
      'solarization': 'sustainability',
      'waste-management': 'sustainability',
      'carbon-footprint': 'sustainability',
      'coverage-data': 'coverage',
      'new-vaccine-intro': 'coverage',
      'outbreaks': 'coverage',
      'budgets': 'coverage',
      'gavi-reporting-indicators': 'gavi'
    };
    
    return pageToTabMap[currentPage] || 'analytics';
  };

  const [openTab, setOpenTab] = useState(getInitialTab());
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileTabKey, setExpandedMobileTabKey] = useState(null);

  return (
    <div className={`topbar-container ${openTab ? 'submenu-open' : ''}`}>
      {/* Platform Header */}
      <div className="platform-header">
        <div className="platform-info">
          <div>
            <h1 className="platform-title">THRIVE360 Suite</h1>
            <p className="platform-subtitle">Comprehensive tools for vaccine and cold chain management</p>
          </div>
        </div>
        <div className="platform-actions">
          <div className="ml-4">
            <label htmlFor="lang-select" className="sr-only">Language</label>
            <select
              id="lang-select"
              value={currentLang}
              onChange={handleLanguageChange}
              className="ml-4 px-3 py-1 border rounded text-sm whitespace-nowrap"
              aria-label="Change language"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="fr">FR</option>
            </select>
          </div>
          <button
            type="button"
            className="mobile-menu-button mobile-only"
            aria-label="Open menu"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <i className="fa-solid fa-bars"></i>
          </button>
          {user && user.email && (
            <>
              <div className="user-info">
                <div className="user-name">Hello {user.name}</div>
                <div className="user-email">{user.email}</div>
                {user.country && (
                  <div className="user-country">Role: {user.accessLevel}</div> 
                )}
              </div>
              {(user.accessLevel === 'admin' || user.accessLevel === 'country-admin' || (user.accessLevel === 'global' && String(userClaims?.role || '').toLowerCase() === 'admin')) && (
                <a href="/admin" className="admin-link ml-4 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm whitespace-nowrap">
                  Admin Panel
                </a>
              )}
              <button
                onClick={handleLogout}
                className="ml-4 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm whitespace-nowrap"
              >
                Sign Out
              </button>
            </>
          )}
        </div>
      </div>
      
      <nav className="menu-bar desktop-only">
        <ul className="bar-container">
          {menuData.map((menu) => (
            <li
              key={menu.key}
              className={`bar-item ${menu.key} ${openTab === menu.key ? 'open' : ''}`}
            >
              <a
                href={`#${menu.key}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenTab(menu.key);
                }}
                className="main-category-link"
              >
                <i className={`fa-solid ${menu.icon}`}></i><span>{menu.label}</span>
              </a>
              <ul className="sub-menu">
                {menu.items.map((item, idx) => (
                  <li key={idx} className="sub-menu-item">
                    <a href={item.path || `/${item.title.toLowerCase().replace(/[\s/]+/g, '-')}`} className="sub-menu-link">
                      <i className={`fa-solid ${item.icon}`}></i>
                      <span className="sub-title">{item.title}</span>
                      <span className="sub-desc">{item.desc}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      {isMobileMenuOpen && (
        <>
          <div
            className="mobile-drawer-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <div className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`} role="dialog" aria-modal="true">
            <div className="mobile-drawer-header">
              <span>Menu</span>
              <button
                type="button"
                className="mobile-drawer-close"
                aria-label="Close menu"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <ul className="mobile-menu-list">
              {menuData.map((menu) => (
                <li key={menu.key} className="mobile-menu-item">
                  <button
                    type="button"
                    className="mobile-accordion-trigger"
                    aria-expanded={expandedMobileTabKey === menu.key}
                    aria-controls={`mobile-submenu-${menu.key}`}
                    onClick={() =>
                      setExpandedMobileTabKey((prev) => (prev === menu.key ? null : menu.key))
                    }
                  >
                    <span className="mobile-accordion-title">
                      <i className={`fa-solid ${menu.icon}`}></i>
                      <span>{menu.label}</span>
                    </span>
                    <i className={`fa-solid ${expandedMobileTabKey === menu.key ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                  </button>
                  <ul
                    id={`mobile-submenu-${menu.key}`}
                    className={`mobile-submenu ${expandedMobileTabKey === menu.key ? 'open' : ''}`}
                  >
                    {menu.items.map((item, idx) => (
                      <li key={idx}>
                        <a
                          href={item.path || `/${item.title.toLowerCase().replace(/[\s/]+/g, '-')}`}
                          onClick={() => {
                            setOpenTab(menu.key);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <i className={`fa-solid ${item.icon}`}></i>
                          <span className="sub-title">{item.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default TopBar;
